import { getAuthHeaders } from '@/hooks/useAuth';

const jsonHeaders = () => ({ ...getAuthHeaders(), 'Content-Type': 'application/json' });

export type RecordMode = 'guest_only' | 'side_by_side' | 'guest_plus_host_audio';

export interface RecordControl { state: 'idle' | 'countdown' | 'recording'; takeId: string | null; startAtEpochMs: number; mode: RecordMode; }
export interface GuestState { inCall: boolean; camReady: boolean; recording: boolean; uploadPct: number | null; mobileBrowser?: boolean; }

export interface InterviewClip { s3Key: string; startedAtMs: number; durationSec: number; status: string; }
export interface InterviewTake {
  id: string; mode: RecordMode; startedAt: string; endedAt: string | null;
  guestName?: string | null;
  guestClip: InterviewClip; hostClip: InterviewClip | null;
  merged: { s3Key: string | null; thumbnailUrl: string | null; status: string; jobId: string | null; error: string | null };
  attachedLesson: { productId: string; moduleId: string; lessonId: string } | null;
  archived: boolean;
}

export interface CastingShadow { studioId: string; sessionId: string; groupId: string; recordId: string; }
export interface Interview {
  _id: string; title: string; productId: string | null; roomName: string; guestToken: string;
  casting?: CastingShadow | null;
  guestName: string | null; guestLastSeenAt: string | null;
  guestState: GuestState; recordControl: RecordControl; takes: InterviewTake[]; status: string;
}

export interface GuestView {
  title: string; creatorName: string; domain: string; roomName: string;
  status: string; recordControl: RecordControl; serverNowMs: number;
}

// --- host ---
export async function createInterview(body: { title: string; productId?: string | null }): Promise<{ interview: Interview; guestUrl: string }> {
  const res = await fetch('/api/interviews', { method: 'POST', headers: jsonHeaders(), body: JSON.stringify(body) });
  if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error || 'Failed to create interview');
  return res.json();
}
export async function listInterviews(productId?: string): Promise<Interview[]> {
  const res = await fetch(`/api/interviews${productId ? `?productId=${productId}` : ''}`, { headers: getAuthHeaders() });
  if (!res.ok) throw new Error('Failed to list interviews');
  return (await res.json()).interviews;
}
export async function getInterview(id: string): Promise<{ interview: Interview; guestUrl: string }> {
  const res = await fetch(`/api/interviews/${id}`, { headers: getAuthHeaders() });
  if (!res.ok) throw new Error('Interview not found');
  return res.json();
}
export async function patchInterview(id: string, body: { title?: string; status?: 'active' | 'archived' }): Promise<void> {
  const res = await fetch(`/api/interviews/${id}`, { method: 'PATCH', headers: jsonHeaders(), body: JSON.stringify(body) });
  if (!res.ok) throw new Error('Failed to update');
}
export async function regenerateLink(id: string): Promise<{ guestToken: string; guestUrl: string }> {
  const res = await fetch(`/api/interviews/${id}/regenerate-link`, { method: 'POST', headers: jsonHeaders() });
  if (!res.ok) throw new Error('Failed to regenerate link');
  return res.json();
}
export async function hostJoin(id: string): Promise<{ domain: string; roomName: string; jwt: string }> {
  const res = await fetch(`/api/interviews/${id}/host-join`, { method: 'POST', headers: jsonHeaders() });
  if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error || 'Meeting unavailable');
  return res.json();
}
export async function recordStart(id: string, mode: RecordMode): Promise<{ recordControl: RecordControl; serverNowMs: number }> {
  const res = await fetch(`/api/interviews/${id}/record/start`, { method: 'POST', headers: jsonHeaders(), body: JSON.stringify({ mode }) });
  if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error || 'Could not start');
  return res.json();
}
export async function recordStop(id: string): Promise<void> {
  const res = await fetch(`/api/interviews/${id}/record/stop`, { method: 'POST', headers: jsonHeaders() });
  if (!res.ok) throw new Error('Could not stop');
}
// Ask the worker to pull the phone's upload into the session right now.
export async function harvestNow(id: string): Promise<void> {
  await fetch(`/api/interviews/${id}/harvest-now`, { method: 'POST', headers: getAuthHeaders() }).catch(() => {});
}

export async function remergeTake(id: string, takeId: string, mode?: RecordMode): Promise<void> {
  const res = await fetch(`/api/interviews/${id}/takes/${takeId}/merge`, { method: 'POST', headers: jsonHeaders(), body: JSON.stringify(mode ? { mode } : {}) });
  if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error || 'Could not re-merge');
}
export async function patchTake(id: string, takeId: string, body: { archived?: boolean; attachedLesson?: { productId: string; moduleId: string; lessonId: string } | null }): Promise<void> {
  const res = await fetch(`/api/interviews/${id}/takes/${takeId}`, { method: 'PATCH', headers: jsonHeaders(), body: JSON.stringify(body) });
  if (!res.ok) throw new Error('Could not update take');
}

// --- curriculum video library (interview clips recorded for a product) ---
export interface LibraryClip {
  takeId: string; interviewId: string; guestName: string; mode: RecordMode;
  s3Key: string; thumbnailUrl: string | null; durationSec: number; startedAt: string | null;
}
export async function listInterviewClips(productId: string): Promise<LibraryClip[]> {
  const res = await fetch(`/api/creator/products/${productId}/interview-clips`, { headers: getAuthHeaders() });
  if (!res.ok) throw new Error('Failed to load clips');
  return (await res.json()).clips;
}
export async function copyInterviewClip(s3Key: string, thumbnailUrl?: string | null): Promise<{ s3Key: string; thumbnailUrl: string | null }> {
  const res = await fetch('/api/creator/products/copy-interview-clip', { method: 'POST', headers: jsonHeaders(), body: JSON.stringify({ s3Key, thumbnailUrl }) });
  if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error || 'Could not use clip');
  return res.json();
}

// --- guest (token-authed, public) ---
export async function guestGet(token: string): Promise<GuestView> {
  const res = await fetch(`/api/interview-guest/${token}`);
  if (res.status === 404) throw new Error('gone');
  if (!res.ok) throw new Error('Failed');
  return res.json();
}
export async function guestState(token: string, body: Partial<{ name: string; inCall: boolean; camReady: boolean; recording: boolean; uploadPct: number | null; mobileBrowser: boolean }>): Promise<{ serverNowMs: number }> {
  const res = await fetch(`/api/interview-guest/${token}/state`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
  if (!res.ok) throw new Error('Failed');
  return res.json();
}
